import { useCallback, useState } from "react";

import {
  Button,
  InfoBar,
  Link,
  PageContainer,
  SpinnerCenter,
  useRemoteConfig,
} from "@shapr3d/frontend-lib";

import CTARow from "../components/CTARow/CTARow";
import { CTARowDivider } from "../components/CTARowDivider/CTARowDivider";
import DeleteAccountModal from "../components/DeleteAccountModal/DeleteAccountModal";
import EmailChangeModal from "../components/EmailChangeModal/EmailChangeModal";
import NameChangeModal from "../components/NameChangeModal/NameChangeModal";
import PasswordChangeModal from "../components/PasswordChangeModal/PasswordChangeModal";
import RecoveryCodeModal from "../components/RecoveryCodeModal/RecoveryCodeModal";
import SetupTwoFactorModal from "../components/SetupTwoFactorModal/SetupTwoFactorModal";
import TwoFactorDisableModal from "../components/TwoFactorDisableModal/TwoFactorDisableModal";
import TwoFactorEnabledModal from "../components/TwoFactorEnabledModal/TwoFactorEnabledModal";
import UnderMaintenanceOrChildren from "../components/UnderMaintenanceOrChildren/UnderMaintenanceOrChildren";
import useAccount from "../hooks/useAccount";
import { useResendVerificationEmail } from "../hooks/useAuth";
import { useTracker } from "../hooks/useTracker";
import { ElementClickAction } from "../lib/TrackingAdapter";

import style from "./AccountPage.module.css";

const AccountPage = () => {
  const tracker = useTracker();
  const { data: accountData, isLoading: isAccountLoading } = useAccount();
  const isTwoFactorFeatureEnabled = useRemoteConfig("web_2fa_totp-ff");

  const {
    mutate: resendVerificationEmail,
    isPending: isResendingVerification,
    isSuccess: isResendVerificationSuccess,
  } = useResendVerificationEmail();

  const [isNameChangeModalOpen, setIsNameChangeModalOpen] = useState(false);
  const [isEmailChangeModalOpen, setIsEmailChangeModalOpen] = useState(false);
  const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] = useState(false);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isSetupTwoFactorModalOpen, setIsSetupTwoFactorModalOpen] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [isRecoveryCodeModalOpen, setIsRecoveryCodeModalOpen] = useState(false);
  const [isTwoFactorEnabledModalOpen, setIsTwoFactorEnabledModalOpen] = useState(false);
  const [isTwoFactorDisableModalOpen, setIsTwoFactorDisableModalOpen] = useState(false);

  const handleResendVerificationEmail = useCallback(() => {
    resendVerificationEmail();
  }, [resendVerificationEmail]);

  if (isAccountLoading || !accountData || isTwoFactorFeatureEnabled == undefined) {
    return (
      <PageContainer>
        <SpinnerCenter />
      </PageContainer>
    );
  }

  return (
    <UnderMaintenanceOrChildren>
      <>
        <PageContainer title="Account">
          <div className={style.container}>
            <CTARow
              title="Your name"
              text={<div className={style.userInputText}>{accountData.name ?? "-"}</div>}
              CTA={
                <Button
                  variant="borderless"
                  onClick={() => {
                    setIsNameChangeModalOpen(true);
                    tracker.trackButtonClick({
                      action: ElementClickAction.CHANGE_NAME,
                    });
                  }}
                >
                  Edit
                </Button>
              }
            />
            <CTARowDivider />
            <CTARow
              title="Email"
              text={
                <div>
                  {accountData.ssoEnforced && (
                    <div className={style.ssoText}>
                      SSO is enabled. Email is controlled by your company’s identity provider.
                    </div>
                  )}
                  <div className={style.userInputText}>{accountData.email}</div>
                </div>
              }
              CTA={
                !accountData.ssoEnforced && (
                  <Button
                    variant="borderless"
                    onClick={() => {
                      setIsEmailChangeModalOpen(true);
                      tracker.trackButtonClick({
                        elementId: "open-email-change-button",
                        action: ElementClickAction.CHANGE_EMAIL,
                      });
                    }}
                  >
                    Edit
                  </Button>
                )
              }
            />
            <CTARowDivider />
            <CTARow
              title="Password"
              text={
                <div>
                  {accountData.ssoEnforced && (
                    <div className={style.ssoText}>
                      SSO is enabled. Password is controlled by your company’s identity provider.
                    </div>
                  )}
                  <span>*********</span>
                </div>
              }
              CTA={
                !accountData.ssoEnforced && (
                  <Button
                    variant="borderless"
                    onClick={() => {
                      setIsPasswordChangeModalOpen(true);
                      tracker.trackButtonClick({
                        action: ElementClickAction.CHANGE_PASSWORD,
                      });
                    }}
                  >
                    Edit
                  </Button>
                )
              }
            />
            {isTwoFactorFeatureEnabled && (
              <>
                <CTARowDivider />
                <CTARow
                  title="Two-factor authentication (2FA)"
                  text={
                    <div>
                      {accountData.ssoEnforced && (
                        <div>
                          SSO is enabled. 2FA is controlled by your company’s identity provider.
                        </div>
                      )}
                      {!accountData.ssoEnforced && (
                        <>
                          {accountData.twoFactorTotpEnabled && (
                            <div>
                              2FA is enabled. You will be prompted to enter a 6-digit code from your
                              authenticator app at login and when performing certain actions
                            </div>
                          )}

                          {!accountData.twoFactorTotpEnabled && (
                            <div>
                              2FA is currently disabled. Enable this to strengthen account security
                              with a one-time code required at login
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  }
                  CTA={
                    !accountData.ssoEnforced && (
                      <>
                        {accountData.twoFactorTotpEnabled && (
                          <Button
                            variant="borderless"
                            className={style.danger}
                            onClick={() => {
                              setIsTwoFactorDisableModalOpen(true);
                            }}
                          >
                            Disable
                          </Button>
                        )}
                        {!accountData.twoFactorTotpEnabled && !accountData.emailVerified && (
                          <InfoBar
                            variant="warning"
                            text={
                              <div>
                                Verify your email address to enable 2FA
                                <br />
                                {isResendVerificationSuccess ? (
                                  <span>Verification email sent! Check your inbox.</span>
                                ) : (
                                  <>
                                    {isResendingVerification ? (
                                      <span>Sending...</span>
                                    ) : (
                                      <Link onClick={handleResendVerificationEmail}>
                                        Resend verification email
                                      </Link>
                                    )}
                                  </>
                                )}
                              </div>
                            }
                          />
                        )}

                        {!accountData.twoFactorTotpEnabled && accountData.emailVerified && (
                          <Button
                            variant="borderless"
                            onClick={() => {
                              setIsSetupTwoFactorModalOpen(true);
                            }}
                          >
                            Enable
                          </Button>
                        )}
                      </>
                    )
                  }
                />
              </>
            )}
            <CTARowDivider />
            <CTARow
              className={style.deleteAccount}
              title="Delete account"
              text="Permanently delete this account and all the projects."
              CTA={
                <Button
                  variant="borderless"
                  className={style.danger}
                  onClick={() => {
                    setIsDeleteAccountModalOpen(true);
                    tracker.trackButtonClick({
                      elementId: "open-delete-account-button",
                      action: ElementClickAction.DELETE_ACCOUNT_MODAL_OPENED,
                    });
                  }}
                >
                  Delete account
                </Button>
              }
            />
          </div>
        </PageContainer>

        <NameChangeModal
          onClose={() => setIsNameChangeModalOpen(false)}
          name={accountData.name}
          open={isNameChangeModalOpen}
        ></NameChangeModal>

        <EmailChangeModal
          onClose={() => setIsEmailChangeModalOpen(false)}
          open={isEmailChangeModalOpen && !accountData.ssoEnforced}
        />
        <PasswordChangeModal
          onClose={() => setIsPasswordChangeModalOpen(false)}
          open={isPasswordChangeModalOpen && !accountData.ssoEnforced}
        ></PasswordChangeModal>
        <DeleteAccountModal
          onClose={() => setIsDeleteAccountModalOpen(false)}
          open={isDeleteAccountModalOpen}
        />

        <SetupTwoFactorModal
          open={isSetupTwoFactorModalOpen}
          onClose={() => setIsSetupTwoFactorModalOpen(false)}
          onSuccess={(code) => {
            setRecoveryCode(code);
            setIsSetupTwoFactorModalOpen(false);
            setIsRecoveryCodeModalOpen(true);
          }}
        />

        <RecoveryCodeModal
          open={isRecoveryCodeModalOpen && Boolean(recoveryCode)}
          recoveryCode={recoveryCode ?? ""}
          onClose={() => {
            setRecoveryCode(null);
            setIsRecoveryCodeModalOpen(false);
            setIsTwoFactorEnabledModalOpen(true);
          }}
        />

        <TwoFactorEnabledModal
          open={isTwoFactorEnabledModalOpen}
          onClose={() => {
            setIsTwoFactorEnabledModalOpen(false);
          }}
        />

        <TwoFactorDisableModal
          open={isTwoFactorDisableModalOpen}
          onClose={() => {
            setIsTwoFactorDisableModalOpen(false);
          }}
        />
      </>
    </UnderMaintenanceOrChildren>
  );
};

export default AccountPage;
